import {
    type AgentSubscriber,
    buildResumeArray,
    type CustomEvent,
    type Interrupt,
    isInterruptExpired,
    type Message,
    type RunAgentParameters,
    randomUUID,
    type UserMessage,
} from '@ag-ui/client';
import is from '@sindresorhus/is';
import noop from 'lodash/noop';

import {
    type AgUiCobrainerCommand,
    type AgUiForwardedProps,
    type AgUiUserMessageCommand,
    agUiEventName,
    agUiTextDiscardedValueSchema,
    agUiThreadStatusValueSchema,
    agUiUserMetadataSchema,
    type ChatResumePayload,
    type ContextRefreshEvent,
    contextRefreshEventSchema,
    type FrontendContext,
    type ThreadRunStatus,
    toAgUiFrontendContext,
} from '../data-contract/chat';
import { busyRetryDelayMs } from './busy-retry';
import {
    type ChatAgentError,
    type ChatHistoryStatus,
    type ChatNotice,
    type ChatRunOutcome,
    type ChatRunPhase,
    IDLE_RUN,
    isVisibleRunError,
} from './chat-run';
import type { CobrainerChatAgent } from './cobrainer-chat-agent';
import { collapseToFirstTurn } from './collapse-to-first-turn';
import { InterruptExpiryTimer } from './interrupt-expiry-timer';

/** A new turn: the command sent to the server, the page it was sent from, and the user message it opens with. */
export type ChatUserTurn = {
    readonly command: AgUiUserMessageCommand;
    readonly frontendContext?: FrontendContext;
    readonly message: UserMessage;
};

/** An interrupt the thread's latest run ended on: answerable until it expires, then only cancellable. */
export type ChatOpenInterrupt = {
    readonly id: string;
    readonly isExpired: boolean;
};

export type ChatAgentSnapshot = {
    /** An agent action the server rejected before its run started, kept so the request stays visible. */
    readonly failedSend: UserMessage | null;
    readonly history: ChatHistoryStatus;
    readonly messages: readonly Message[];
    readonly notice: ChatNotice | null;
    /** What the latest finished run (own or sync) left waiting on the user; the next own run covers them. */
    readonly openInterrupts: readonly ChatOpenInterrupt[];
    /** The thread's own run. Sync runs never show up here, unlike the client's `agent.isRunning`. */
    readonly run: ChatRunPhase;
    readonly threadStatus: ThreadRunStatus;
};

export type ChatRunSettlement = {
    /**
     * The run finished an agent-action turn: the thread is idle again, and the turn delivered a live
     * context refresh and showed no error. Only such a turn resolved the request.
     */
    readonly resolvesAgentAction: boolean;
    readonly threadId: string;
};

/**
 * The logical turn: a new command starts one, answers and continues carry it on. It outlives the
 * session, which the registry may evict while the turn waits on a question or was stopped.
 */
export type ChatTurnMemory = {
    isAgentAction: boolean;
    /** The user stopped the turn, so a resumable thread is not resumed automatically. */
    isStopped: boolean;
    sawContextRefresh: boolean;
    /** Only a new command clears it: no later success of an errored turn may close the dock. */
    sawVisibleError: boolean;
};

export const createChatTurnMemory = (): ChatTurnMemory => ({
    isAgentAction: false,
    isStopped: false,
    sawContextRefresh: false,
    sawVisibleError: false,
});

/** A custom event the stream carried but the session could not read; the consumer decides where to report it. */
export type DroppedChatEvent = {
    readonly eventType: string;
    readonly issues: readonly { readonly code: string; readonly path: string }[];
    readonly threadId: string;
};

export type ChatLiveActivity = {
    readonly activityType: string;
    readonly content: unknown;
    readonly messageId: string;
    readonly threadId: string;
};

export type ThreadAgentSessionOptions = {
    readonly agent: CobrainerChatAgent;
    readonly isVisible: () => boolean;
    readonly onChange: () => void;
    readonly onContextRefresh: (refresh: ContextRefreshEvent) => void;
    readonly onDroppedEvent: (dropped: DroppedChatEvent) => void;
    /** Called whenever the session has no queued work, so the registry may evict it. */
    readonly onIdle: () => void;
    readonly onLiveActivity?: (activity: ChatLiveActivity) => void;
    readonly onRunSettled: (settlement: ChatRunSettlement) => void;
    readonly onRunStarted: () => void;
    readonly pollIntervalMs: number;
    readonly threadId: string;
    readonly turn: ChatTurnMemory;
};

export const EMPTY_CHAT_AGENT_SNAPSHOT: ChatAgentSnapshot = {
    failedSend: null,
    history: 'unloaded',
    messages: [],
    notice: null,
    openInterrupts: [],
    run: IDLE_RUN,
    threadStatus: 'idle',
};

type LiveRun = {
    readonly anchorMessageId: string | null;
    error: ChatAgentError | null;
    failure: unknown;
    hasFailed: boolean;
    hasStarted: boolean;
    isStopped: boolean;
    /** The text messages the run streamed: the server keeps none of them unless the run finishes. */
    readonly streamedTextIds: Set<string>;
    terminal: 'aborted' | 'error' | 'interrupt' | 'success' | null;
};

/** What started a run; it decides how a rejection before the run started is reported. */
type RunKind = 'answer' | 'continue' | 'retry' | 'send';

/** The callbacks of a run a user operation waits on. */
type RunCallbacks = {
    /** The server accepted the run (RUN_STARTED). */
    readonly onStarted: () => void;
    /** The run ended without having started. */
    readonly onUnstarted: (outcome: ChatRunOutcome) => void;
};

const NO_RUN_CALLBACKS: RunCallbacks = { onStarted: noop, onUnstarted: noop };

type SettledRun = Extract<ChatRunPhase, { readonly phase: 'settled' }>;

const STREAM_CLOSED_ERROR: ChatAgentError = { code: 'STREAM_CLOSED', message: 'Chat stream closed' };

const HTTP_CONFLICT = 409;

const HTTP_TOO_MANY_REQUESTS = 429;

const INTERRUPT_EXPIRED = 'INTERRUPT_EXPIRED';

const HISTORY_MISMATCH = 'HISTORY_MISMATCH';

type ObservedSync = {
    errorCode: string | null;
    failed: boolean;
    hasFinished: boolean;
    hasSnapshot: boolean;
    status: ThreadRunStatus | null;
};

/**
 * What the user is told about a request turned away for any reason but a busy thread; a continue is
 * retried silently. A retry the server refuses as a conflict (nothing failed to retry, or a bulk run
 * still works on the turn) shows the thread as the server holds it, read again after the run.
 */
const rejectionNoticeOf = (kind: RunKind, error: ChatAgentError, status: number | null): ChatNotice | null => {
    switch (kind) {
        case 'answer':
            return error.code === INTERRUPT_EXPIRED ? 'question-expired' : 'answer-failed';
        case 'continue':
            return null;
        case 'retry':
            return status === HTTP_CONFLICT ? null : 'send-failed';
        case 'send':
            return 'send-failed';
    }
};

const isAbortError = (error: unknown): boolean => is.object(error) && 'name' in error && error.name === 'AbortError';

const httpStatusOf = (error: unknown): number | null =>
    is.object(error) && 'status' in error && is.number(error.status) ? error.status : null;

const toFailureError = (error: unknown): ChatAgentError => {
    const status = httpStatusOf(error);
    return {
        code: is.null(status) ? 'NETWORK_ERROR' : `HTTP_${status}`,
        message: is.error(error) ? error.message : String(error),
    };
};

const lastUserMessageId = (messages: readonly Message[]): string | null =>
    messages.findLast((message) => message.role === 'user')?.id ?? null;

/** The thread status a `cobrainer.thread-status` event reports, or null for any other event. */
const parseThreadStatusEvent = (event: CustomEvent): ThreadRunStatus | null => {
    if (event.name !== agUiEventName.threadStatus) return null;
    const parsed = agUiThreadStatusValueSchema.safeParse(event.value);
    return parsed.success ? parsed.data.threadStatus : null;
};

/** The streamed text a `cobrainer.text-discarded` event takes back, or null for any other event. */
const parseTextDiscardedEvent = (event: CustomEvent): string | null => {
    if (event.name !== agUiEventName.textDiscarded) return null;
    const parsed = agUiTextDiscardedValueSchema.safeParse(event.value);
    return parsed.success ? parsed.data.messageId : null;
};

const forwardedPropsOf = (command: AgUiCobrainerCommand): AgUiForwardedProps => ({ cobrainer: command });

const readUserCommand = (message: UserMessage): AgUiUserMessageCommand | null =>
    agUiUserMetadataSchema.safeParse(message.metadata).data?.cobrainer.command ?? null;

const CONTINUE_PARAMETERS: RunAgentParameters = { forwardedProps: forwardedPropsOf({ type: 'continue' }) };

type ResumeResponses = Parameters<typeof buildResumeArray>[1];

/** Cancels every open interrupt: a new message moves the thread past them, expired or not. */
const cancelAll = (interrupts: readonly Interrupt[]): ResumeResponses =>
    Object.fromEntries(interrupts.map(({ id }) => [id, { status: 'cancelled' }]));

/**
 * One thread's agent: a serial queue so sync runs and own runs never overlap, the own-run
 * lifecycle, a sync after a run that broke off, polling while another run holds the thread, and continuing a
 * dropped turn. The thread's open interrupts are the client's own `pendingInterrupts`: set by the run
 * or sync run that ended on them, and covered by the resume entries of the next own run.
 *
 * @internal Created and owned by ChatAgentRegistry; use the registry everywhere else.
 */
export class ThreadAgentSession {
    readonly threadId: string;
    private readonly agent: CobrainerChatAgent;
    private autoResumed = false;
    /** Consecutive busy answers to continues since the last accepted run; doubles the fallback delay. */
    private busyStreak = 0;
    /** Until when another run holds the thread, by its Retry-After; nothing is retried before. */
    private busyUntil: number | null = null;
    private readonly expiryTimer = new InterruptExpiryTimer(() => this.refresh());
    private failedSend: UserMessage | null = null;
    private history: ChatHistoryStatus = 'unloaded';
    private isDisposed = false;
    private isFresh = false;
    private readonly listeners = new Set<() => void>();
    private liveRun: LiveRun | null = null;
    private readonly options: ThreadAgentSessionOptions;
    private pendingNotice: ChatNotice | null = null;
    private pendingOperations = 0;
    private pollTimer: ReturnType<typeof setTimeout> | null = null;
    /** Withdraws the send still waiting in the queue; there is at most one. */
    private withdrawQueuedSend: (() => void) | null = null;
    private run: ChatRunPhase = IDLE_RUN;
    private shownMessages: readonly Message[] = [];
    private snapshotCache: ChatAgentSnapshot = EMPTY_CHAT_AGENT_SNAPSHOT;
    private tail: Promise<void> = Promise.resolve();
    private threadStatus: ThreadRunStatus = 'idle';
    private readonly unsubscribeAgent: () => void;

    constructor(options: ThreadAgentSessionOptions) {
        this.options = options;
        this.threadId = options.threadId;
        this.agent = options.agent;
        const bump = (): void => this.refresh();
        this.unsubscribeAgent = this.agent.subscribe({
            onMessagesChanged: bump,
            onRunFinalized: bump,
            onRunInitialized: bump,
        }).unsubscribe;
        this.snapshotCache = this.buildSnapshot();
    }

    private get turn(): ChatTurnMemory {
        return this.options.turn;
    }

    get snapshot(): ChatAgentSnapshot {
        return this.snapshotCache;
    }

    get isRunning(): boolean {
        return !is.null(this.liveRun);
    }

    /** Nothing queued, no own run and nobody watching: safe to evict. */
    get isIdle(): boolean {
        return this.pendingOperations === 0 && is.null(this.liveRun) && this.listeners.size === 0;
    }

    subscribe(listener: () => void): () => void {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }

    /** A thread created in this session has no history to load, so it skips the initial sync run. */
    markFresh(): void {
        this.history = 'loaded';
        this.isFresh = true;
        this.refresh();
    }

    /** Loads history on first use, and syncs again on reactivation while nothing is in flight. */
    activate(): void {
        if (this.isFresh || this.pendingOperations > 0 || this.isRunning) return;
        void this.sync();
    }

    sync(): Promise<void> {
        return this.enqueue(() => this.runSync());
    }

    /**
     * Resolves true once the server accepted the run (RUN_STARTED), false when the send was rejected,
     * so callers keep the user's input until then. A cached `running` status never blocks the send:
     * the server answers 429 if another run still holds the thread.
     */
    send(build: () => ChatUserTurn): Promise<boolean> {
        if (this.isRunning || !is.null(this.withdrawQueuedSend)) {
            this.pendingNotice = 'busy';
            this.refresh();
            return Promise.resolve(false);
        }

        return new Promise<boolean>((resolve) => {
            let isWithdrawn = false;
            const withdraw = (): void => {
                isWithdrawn = true;
                resolve(false);
            };
            this.withdrawQueuedSend = withdraw;
            const dequeue = (): void => {
                if (this.withdrawQueuedSend === withdraw) this.withdrawQueuedSend = null;
            };
            void this.enqueue(async () => {
                try {
                    if (this.isDisposed || isWithdrawn) return;
                    if (this.history === 'unloaded') await this.runSync();
                    // A stop during the send's own history load withdraws it before anything is shown or sent.
                    if (this.isDisposed || isWithdrawn) return;
                    // From here on a stop aborts the run instead of withdrawing the send.
                    dequeue();
                    await this.runSend(build(), () => resolve(true));
                } finally {
                    dequeue();
                    resolve(false);
                }
            });
        });
    }

    /**
     * Answers an open interrupt with a run that resolves it and continues the turn. Resolves once that
     * run settled (and, if it broke off, the thread was read again): true when the server started it,
     * false when there was nothing to answer or the server turned it away. An expired interrupt is
     * never resolved.
     */
    answer(interruptId: string, payload: ChatResumePayload): Promise<boolean> {
        let hasStarted = false;
        return this.enqueue(async () => {
            if (this.isDisposed) return;
            if (this.history === 'unloaded') await this.runSync();
            if (this.isDisposed) return;
            const interrupt = this.agent.pendingInterrupts.find(({ id }) => id === interruptId);
            const isExpired = !is.undefined(interrupt) && isInterruptExpired(interrupt);
            const resume = isExpired ? null : this.resumeEntries({ [interruptId]: { payload, status: 'resolved' } });
            if (is.null(resume) || is.undefined(interrupt)) {
                this.pendingNotice = isExpired ? 'question-expired' : 'answer-failed';
                this.refresh();
                return;
            }
            this.turn.isStopped = false;
            this.autoResumed = false;
            await this.executeRun(
                'answer',
                lastUserMessageId(this.agent.messages),
                { resume },
                {
                    onStarted: () => {
                        hasStarted = true;
                    },
                    onUnstarted: noop,
                },
            );
        })
            .catch(noop)
            .then(() => hasStarted);
    }

    /**
     * Runs the failed latest turn again: the run shows the thread up to the turn's stored user message
     * `userMessageId`, and the server replaces the failed reply without storing the message twice.
     * Resolves true once the server started the run; false when the message is not the thread's latest
     * request or the server turned the retry away, after which the thread is read again.
     */
    retry(userMessageId: string): Promise<boolean> {
        if (this.isRunning || !is.null(this.withdrawQueuedSend)) {
            this.pendingNotice = 'busy';
            this.refresh();
            return Promise.resolve(false);
        }
        let hasStarted = false;
        return this.enqueue(async () => {
            if (this.isDisposed) return;
            if (this.history === 'unloaded') await this.runSync();
            if (this.isDisposed) return;
            const messages = this.agent.messages;
            const index = messages.findIndex(({ id }) => id === userMessageId);
            const request = messages[index];
            if (request?.role !== 'user' || lastUserMessageId(messages) !== userMessageId) return;
            // The failed reply leaves the thread now, like the server drops it once the run starts.
            this.agent.setMessages(messages.slice(0, index + 1));
            const command = readUserCommand(request);
            Object.assign(this.turn, {
                isAgentAction: !is.null(command) && command.type !== 'new',
                isStopped: false,
                sawContextRefresh: false,
            });
            this.autoResumed = false;
            await this.executeRun(
                'retry',
                userMessageId,
                { forwardedProps: forwardedPropsOf({ type: 'retry' }) },
                {
                    onStarted: () => {
                        hasStarted = true;
                    },
                    onUnstarted: noop,
                },
            );
        })
            .catch(noop)
            .then(() => hasStarted);
    }

    /**
     * Aborts the own run and withdraws sends still waiting in the queue (they settle as not
     * started). Not queued itself, so it can interrupt the operation in flight.
     */
    stop(): boolean {
        const withdraw = this.withdrawQueuedSend;
        this.withdrawQueuedSend = null;
        withdraw?.();
        if (is.null(this.liveRun) && is.null(withdraw)) return false;
        this.turn.isStopped = true;
        if (!is.null(this.liveRun)) {
            this.liveRun.isStopped = true;
            this.agent.abortRun();
        }
        this.refresh();
        return true;
    }

    /** Aborts any run or sync run in flight; queued operations are skipped and their callers settled. */
    dispose(): void {
        this.isDisposed = true;
        this.clearPoll();
        this.expiryTimer.dispose();
        this.agent.abortRun();
        this.unsubscribeAgent();
    }

    private enqueue(operation: () => Promise<void>): Promise<void> {
        this.pendingOperations += 1;
        const result = this.tail.then(operation).finally(() => {
            this.pendingOperations -= 1;
            if (this.pendingOperations === 0 && !this.isDisposed) this.options.onIdle();
        });
        this.tail = result.catch(noop);
        return result;
    }

    /** Opens a turn with the user's message; `onStarted` fires once the server accepted its run. */
    private async runSend({ command, frontendContext, message }: ChatUserTurn, onStarted: () => void): Promise<void> {
        // Built only now, after any history load, so the entries cover what the thread waits on.
        const resume = this.resumeEntries(cancelAll(this.agent.pendingInterrupts)) ?? [];
        this.agent.addMessage(message);
        Object.assign(this.turn, createChatTurnMemory(), { isAgentAction: command.type !== 'new' });
        this.autoResumed = false;
        const parameters: RunAgentParameters = {
            context: is.undefined(frontendContext) ? [] : [toAgUiFrontendContext(frontendContext)],
            forwardedProps: forwardedPropsOf(command),
            ...(resume.length === 0 ? {} : { resume }),
        };
        await this.executeRun('send', message.id, parameters, {
            onStarted,
            // The server never stored the message: it leaves the thread, and a rejected agent action
            // stays visible as a failed request.
            onUnstarted: (outcome) => {
                this.agent.setMessages(this.agent.messages.filter((existing) => existing.id !== message.id));
                if (this.turn.isAgentAction && outcome === 'rejected') this.failedSend = message;
            },
        });
    }

    /** Entries covering every open interrupt, or null when `responses` leave one uncovered. */
    private resumeEntries(responses: ResumeResponses): ReturnType<typeof buildResumeArray> | null {
        try {
            return buildResumeArray(this.agent.pendingInterrupts, responses);
        } catch {
            return null;
        }
    }

    private buildSnapshot(): ChatAgentSnapshot {
        const run: ChatRunPhase = is.null(this.liveRun)
            ? this.run
            : { anchorMessageId: this.liveRun.anchorMessageId, phase: 'running' };
        const now = new Date();
        this.shownMessages = collapseToFirstTurn(this.shownMessages, this.agent.messages);
        return {
            failedSend: this.failedSend,
            history: this.history,
            messages: this.shownMessages,
            notice: this.pendingNotice ?? (this.run.phase === 'settled' ? this.run.rejection : null),
            openInterrupts: this.agent.pendingInterrupts.map((interrupt) => ({
                id: interrupt.id,
                isExpired: isInterruptExpired(interrupt, now),
            })),
            run,
            threadStatus: this.threadStatus,
        };
    }

    private refresh(): void {
        if (this.isDisposed) return;
        this.expiryTimer.track(this.agent.pendingInterrupts);
        this.snapshotCache = this.buildSnapshot();
        for (const listener of this.listeners) listener();
        this.options.onChange();
    }

    private async runSync(): Promise<void> {
        if (this.isDisposed) return;
        this.clearPoll();
        if (this.history === 'unloaded') this.history = 'loading';
        this.refresh();
        let observed = await this.connectSync();
        // The producer does not hold what this client shows (spec §10): read the thread afresh, once.
        if (!this.isDisposed && observed.errorCode === HISTORY_MISMATCH && this.agent.messages.length > 0) {
            this.agent.setMessages([]);
            observed = await this.connectSync();
        }
        if (this.isDisposed) return;
        // A sync run only counts once the history and a terminal arrived; a truncated stream keeps
        // the messages shown so far and the previous status.
        const isLoaded = !observed.failed && observed.hasSnapshot && observed.hasFinished;
        this.history = isLoaded ? 'loaded' : 'failed';
        if (isLoaded) {
            this.threadStatus = observed.status ?? 'idle';
            this.forgetFinishedTurn();
        }
        if (this.threadStatus !== 'running') this.clearBusyNotice();
        this.refresh();
        this.applySyncPolicy();
    }

    private async connectSync(): Promise<ObservedSync> {
        const observed: ObservedSync = {
            errorCode: null,
            failed: false,
            hasFinished: false,
            hasSnapshot: false,
            status: null,
        };
        const subscriber: AgentSubscriber = {
            onCustomEvent: ({ event }) => {
                observed.status = parseThreadStatusEvent(event) ?? observed.status;
            },
            onMessagesSnapshotEvent: () => {
                observed.hasSnapshot = true;
            },
            onRunErrorEvent: ({ event }) => {
                observed.failed = true;
                observed.errorCode = event.code ?? null;
            },
            onRunFailed: () => {
                observed.failed = true;
            },
            onRunFinishedEvent: () => {
                observed.hasFinished = true;
            },
        };
        try {
            await this.agent.connectAgent({ runId: randomUUID() }, subscriber);
        } catch {
            observed.failed = true;
        }
        this.collapseRepeatedMessages();
        return observed;
    }

    private collapseRepeatedMessages(): void {
        const messages = this.agent.messages;
        const collapsed = collapseToFirstTurn([], messages);
        if (collapsed.length < messages.length) this.agent.setMessages(collapsed);
    }

    // The server says nothing is pending: whatever ended the turn (a disconnect, an error), its
    // auto-close decision is behind it, so eviction may drop the record. A stop is kept, so it is
    // never resumed automatically.
    private forgetFinishedTurn(): void {
        const isFinished = this.threadStatus === 'idle' && is.null(this.liveRun);
        if (isFinished && !this.turn.isStopped) Object.assign(this.turn, createChatTurnMemory());
    }

    private clearBusyNotice(): void {
        if (this.pendingNotice === 'busy') this.pendingNotice = null;
        if (this.run.phase === 'settled' && this.run.rejection === 'busy') this.run = { ...this.run, rejection: null };
    }

    private applySyncPolicy(): void {
        if (this.isDisposed || this.isRunning || !this.options.isVisible()) return;
        if (this.threadStatus === 'running') {
            // Once another run holds the thread, the next resumable report is a new stall.
            this.autoResumed = false;
            this.schedulePoll(this.busyRemainingMs() ?? this.options.pollIntervalMs);
            return;
        }
        if (this.threadStatus === 'idle') {
            this.autoResumed = false;
            return;
        }
        // A turn waiting on a question continues only through its answer.
        const isWaitingOnQuestion = this.agent.pendingInterrupts.length > 0;
        const isBlocked = isWaitingOnQuestion || this.turn.isStopped || !is.null(this.withdrawQueuedSend);
        if (isBlocked || this.autoResumed) return;
        // A continue the lock turned away waits out its Retry-After; the poll reads the thread again first.
        const busyRemainingMs = this.busyRemainingMs();
        if (!is.null(busyRemainingMs)) {
            this.schedulePoll(busyRemainingMs);
            return;
        }
        this.autoResumed = true;
        void this.enqueue(() =>
            this.executeRun('continue', lastUserMessageId(this.agent.messages), CONTINUE_PARAMETERS),
        );
    }

    private busyRemainingMs(): number | null {
        if (is.null(this.busyUntil)) return null;
        const remaining = this.busyUntil - Date.now();
        return remaining > 0 ? remaining : null;
    }

    private schedulePoll(delayMs: number): void {
        this.clearPoll();
        this.pollTimer = setTimeout(() => {
            this.pollTimer = null;
            const isIdle = this.pendingOperations === 0 && !this.isRunning;
            if (this.isDisposed || !isIdle || !this.options.isVisible()) return;
            void this.sync();
        }, delayMs);
    }

    private clearPoll(): void {
        if (is.null(this.pollTimer)) return;
        clearTimeout(this.pollTimer);
        this.pollTimer = null;
    }

    private async executeRun(
        kind: RunKind,
        anchorMessageId: string | null,
        parameters: RunAgentParameters,
        { onStarted, onUnstarted }: RunCallbacks = NO_RUN_CALLBACKS,
    ): Promise<void> {
        if (this.isDisposed) return;
        const run: LiveRun = {
            anchorMessageId,
            error: null,
            failure: undefined,
            hasFailed: false,
            hasStarted: false,
            isStopped: false,
            streamedTextIds: new Set(),
            terminal: null,
        };
        this.clearPoll();
        this.liveRun = run;
        this.pendingNotice = null;
        this.failedSend = null;
        this.refresh();

        try {
            await this.agent.runAgent(
                { ...parameters, runId: randomUUID() },
                this.runSubscriber(run, kind, () => {
                    this.busyStreak = 0;
                    this.busyUntil = null;
                    this.options.onRunStarted();
                    onStarted();
                }),
            );
        } catch (error) {
            if (!run.hasFailed) {
                run.hasFailed = true;
                run.failure = error;
            }
        }
        this.collapseRepeatedMessages();

        this.liveRun = null;
        this.isFresh = false;
        if (this.isDisposed) return;
        const settled = this.settle(run, kind);
        const { outcome } = settled;
        this.run = settled;
        if (isVisibleRunError(settled)) this.turn.sawVisibleError = true;
        if (!run.hasStarted) onUnstarted(outcome);
        this.refresh();
        const { isAgentAction, sawContextRefresh, sawVisibleError } = this.turn;
        this.options.onRunSettled({
            resolvesAgentAction:
                outcome === 'success' &&
                this.threadStatus === 'idle' &&
                isAgentAction &&
                sawContextRefresh &&
                !sawVisibleError,
            threadId: this.threadId,
        });
        // A stream that reached its terminal reported the thread's messages and status itself; only
        // one that broke off, or never started, syncs again.
        if (outcome === 'success' || outcome === 'interrupt') {
            this.forgetFinishedTurn();
            return;
        }
        // Text cut off mid-stream was never persisted, so the sync would be turned away for showing it;
        // it stays on screen only when the sync cannot read the thread either.
        const shown = this.agent.messages;
        const persisted = shown.filter(
            (message) => message.role !== 'assistant' || !run.streamedTextIds.has(message.id),
        );
        if (persisted.length < shown.length) this.agent.setMessages(persisted);
        // Showing the same history again would be turned away again: the sync reads the thread afresh.
        if (settled.error?.code === HISTORY_MISMATCH) this.agent.setMessages([]);
        const sent = this.agent.messages;
        await this.runSync();
        if (
            !this.isDisposed &&
            this.history === 'failed' &&
            this.agent.messages === sent &&
            persisted.length < shown.length
        ) {
            this.agent.setMessages(shown);
        }
    }

    private runSubscriber(run: LiveRun, kind: RunKind, onStarted: () => void): AgentSubscriber {
        const onLiveActivity = kind === 'continue' ? undefined : this.options.onLiveActivity;
        return {
            onCustomEvent: ({ event, messages }) => {
                // Text the agent streamed before it went on to call a tool was never its reply.
                const discarded = parseTextDiscardedEvent(event);
                if (is.null(discarded)) {
                    this.handleCustomEvent(event);
                    return undefined;
                }
                run.streamedTextIds.delete(discarded);
                return { messages: messages.filter((message) => message.id !== discarded) };
            },
            onTextMessageStartEvent: ({ event }) => {
                run.streamedTextIds.add(event.messageId);
            },
            onActivitySnapshotEvent: ({ event }) => {
                onLiveActivity?.({
                    activityType: event.activityType,
                    content: event.content,
                    messageId: event.messageId,
                    threadId: this.threadId,
                });
            },
            onRunErrorEvent: ({ event }) => {
                if (event.code === 'abort') {
                    run.terminal = 'aborted';
                    return;
                }
                run.terminal = 'error';
                run.error = { code: event.code ?? 'AGENT_ERROR', message: event.message };
            },
            onRunFailed: ({ error }) => {
                run.hasFailed = true;
                run.failure = error;
            },
            onRunFinishedEvent: ({ outcome }) => {
                run.terminal = outcome === 'cancelled' ? 'aborted' : outcome;
            },
            onRunStartedEvent: () => {
                if (run.hasStarted) return;
                run.hasStarted = true;
                onStarted();
            },
        };
    }

    private settle(run: LiveRun, kind: RunKind): SettledRun {
        const settled = (
            outcome: ChatRunOutcome,
            error: ChatAgentError | null = null,
            rejection: ChatNotice | null = null,
        ) => ({ anchorMessageId: run.anchorMessageId, error, outcome, phase: 'settled', rejection }) as const;

        if (run.isStopped || run.terminal === 'aborted' || (run.hasFailed && isAbortError(run.failure))) {
            return settled('aborted');
        }
        if (!run.hasStarted) return this.settleRejection(run, kind, settled);
        if (run.terminal === 'error') return settled('error', run.error);
        if (!is.null(run.terminal)) return settled(run.terminal);
        return run.hasFailed
            ? settled('error', toFailureError(run.failure))
            : settled('disconnected', STREAM_CLOSED_ERROR);
    }

    private settleRejection(
        run: LiveRun,
        kind: RunKind,
        settled: (outcome: ChatRunOutcome, error?: ChatAgentError | null, rejection?: ChatNotice | null) => SettledRun,
    ): SettledRun {
        const isBusy = httpStatusOf(run.failure) === HTTP_TOO_MANY_REQUESTS;
        if (isBusy) {
            this.threadStatus = 'running';
            if (kind === 'continue') {
                this.busyStreak += 1;
                this.autoResumed = false;
            }
            this.busyUntil = Date.now() + busyRetryDelayMs(this.agent.retryAfterSeconds, this.busyStreak);
        } else {
            this.busyStreak = 0;
        }
        const error = run.error ?? toFailureError(run.failure);
        return settled('rejected', error, isBusy ? 'busy' : rejectionNoticeOf(kind, error, httpStatusOf(run.failure)));
    }

    private handleCustomEvent(event: CustomEvent): void {
        const threadStatus = parseThreadStatusEvent(event);
        if (!is.null(threadStatus)) {
            this.threadStatus = threadStatus;
            return;
        }
        if (event.name !== agUiEventName.contextRefresh) return;
        const parsed = contextRefreshEventSchema.safeParse(event.value);
        if (!parsed.success) {
            this.options.onDroppedEvent({
                eventType: 'context-refresh',
                issues: parsed.error.issues
                    .slice(0, 10)
                    .map((issue) => ({ code: issue.code, path: issue.path.join('.') })),
                threadId: this.threadId,
            });
            return;
        }
        this.turn.sawContextRefresh = true;
        this.options.onContextRefresh(parsed.data);
    }
}
